import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react"
import clsx from "clsx"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { getCdnAssetUrl } from "@/helpers/getCdnAssetUrl"
import { spaceNumber } from "@/helpers/spaceNumber"
import { getTgWebApp } from "@/helpers/tgWebApp"
import { useAppSelector } from "@/store"
import {
  useActivateCardBackByTemplateIdMutation,
  useGetCardBackActiveQuery,
  useGetCardBacksPresenceQuery
} from "@/store/apis/cardBacksAPI"
import { IStoreItem, STORE_ITEM_TYPES } from "@/store/apis/storeAPI"
import {
  IStarPaymentInvoice,
  ITonPaymentInvoice,
  STORE_CURRENCY,
  useCreateInvoiceMutation
} from "@/store/apis/storePaymentsAPI"
import { AppSelectors } from "@/store/slices/app"
import { Button } from "@/ui/Button"
import styles from "./CardSkinItem.module.scss"

interface ICardSkinItem extends IStoreItem<STORE_ITEM_TYPES.CARD_BACK> {}

export const CardSkinItem: FC<ICardSkinItem> = ({
  _id,
  dataId,
  type,
  data,
  pricing
}) => {
  const { t } = useTranslation()
  const { t: tSkins } = useTranslation("skins")
  const senderWallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()

  const storeCurrency = useAppSelector(AppSelectors.selectStoreCurrency())

  const { data: presence } = useGetCardBacksPresenceQuery()
  const { data: activeCardBack } = useGetCardBackActiveQuery()

  const hasSkin = !!(presence?.[dataId] && presence?.[dataId] > 0)
  const isActive = activeCardBack?.cardBack._id === dataId

  const [createInvoice, { isLoading: isCreatingInvoice }] =
    useCreateInvoiceMutation()
  const [activateCardBack, { isLoading: isActivatingCardBack }] =
    useActivateCardBackByTemplateIdMutation()

  const btnText = useMemo(() => {
    if (isActive) {
      return t("button.active")
    }

    if (hasSkin) {
      return t("button.use")
    }

    if (storeCurrency === STORE_CURRENCY.GOLDEN_DUST) {
      return (
        <>
          <CurrencyIcon.goldenDust /> {spaceNumber(pricing.goldenDust)}
        </>
      )
    } else if (storeCurrency === STORE_CURRENCY.TG_STARS) {
      return (
        <>
          <CurrencyIcon.star /> {spaceNumber(pricing.stars)}
        </>
      )
    }

    return (
      <>
        <CurrencyIcon.ton /> {spaceNumber(pricing.ton)}
      </>
    )
  }, [isActive, hasSkin, storeCurrency, pricing, t])

  const handleBuy = async () => {
    if (isActive || isCreatingInvoice || isActivatingCardBack) {
      return
    }

    if (hasSkin) {
      try {
        if (!data) {
          toast.error("Failed to activate card skin")
          return
        }

        await activateCardBack({ templateId: dataId, card: data }).unwrap()
        toast.success("Card skin activated")
      } catch {
        toast.error("Failed to activate card skin")
      }
      return
    }

    if (storeCurrency === STORE_CURRENCY.TG_STARS) {
      try {
        const invoice = (await createInvoice({
          itemId: _id,
          itemType: type,
          currency: storeCurrency,
          price: pricing.stars
        }).unwrap()) as IStarPaymentInvoice

        getTgWebApp().openInvoice(invoice.invoiceLink, (e: string) =>
          console.log(e)
        )
      } catch {
        toast.error("Failed to create invoice")
      }
    } else if (storeCurrency === STORE_CURRENCY.EXTERNAL_TON) {
      try {
        if (!senderWallet?.account?.address) {
          tonConnectUI.openModal()
          return
        }

        const invoice = (await createInvoice({
          itemId: _id,
          itemType: type,
          currency: storeCurrency,
          price: pricing.ton
        }).unwrap()) as ITonPaymentInvoice

        const transaction = {
          validUntil: invoice.validUntil,
          messages: [
            {
              address: invoice.to,
              amount: invoice.amount,
              payload: invoice.payload
            }
          ]
        }

        await tonConnectUI.sendTransaction(transaction, {
          modals: "all",
          notifications: "all"
        })
      } catch {
        toast.error("Failed to create invoice")
      }
    }
  }

  return (
    <div className={clsx(styles.CardSkinItem)} onClick={handleBuy}>
      <div className={clsx(styles.CardSkinItem_header)}>
        <div className={clsx(styles.CardSkinItem_title)}>
          {data?.type ? tSkins(`skinNames.${data.type}`) : null}
        </div>
        <div className={clsx(styles.CardSkinItem_img)}>
          <img
            className={clsx(styles.CardSkinItem_img_back)}
            src={getCdnAssetUrl(data?.backImgSrc)}
            alt="back"
          />
          <img
            className={clsx(styles.CardSkinItem_img_front)}
            src={getCdnAssetUrl(data?.exampleImgSrc)}
            alt="front"
          />
        </div>
      </div>
      <Button
        className={clsx(styles.CardSkinItem_button, {
          [styles.star]: !hasSkin && storeCurrency === STORE_CURRENCY.TG_STARS,
          [styles.ton]:
            !hasSkin && storeCurrency === STORE_CURRENCY.EXTERNAL_TON,
          [styles.has]: !isActive && hasSkin,
          [styles.active]: isActive
        })}
        isLoading={isCreatingInvoice || isActivatingCardBack}
        disabled={isActive}
      >
        {btnText}
      </Button>
    </div>
  )
}
